import React, { useState } from 'react'

// Kendo components
import { Dialog } from '@progress/kendo-react-dialogs'
import { Form, FormElement } from '@progress/kendo-react-form'
import { Grid, GridColumn, GridSortChangeEvent } from '@progress/kendo-react-grid'
import { formatDate } from '@telerik/kendo-intl'
import { orderBy, SortDescriptor } from '@progress/kendo-data-query'

// Custom component
import { FormCheckbox, FormDropDownList, FormInput, FormTextArea } from './FormInput'
import { Certificate, CertificateData, Gateway } from './Interfaces'
import { AddCertificateModal } from './AddCertificateModal'
import { KendoGridDateCell } from './GridData'
import { EditCertificateModal } from './EditCertificateModal'
import { DeleteCertificateCell, TestCertificateCell } from './TableCells'
import { getAPIURL } from '../../GlobalComponents/URLs'

import styles from '.././EDIForms.module.scss'
import { AWCButton, BUTTON_TYPES } from '../../GlobalComponents/Inputs/Inputs'

function getCurrentDate(): any {
    const currentDate = new Date().toLocaleDateString()
    return currentDate
}

export const GatewayModal: React.FC<{
    gateway?: Gateway
    isNewGateway: boolean
    cancelDialog: () => void
    onSubmit: (event: any, itemCertificateView?: Array<Certificate>) => void
    setCertificate: (certificate: Certificate) => void
    deleteCertificate: (certifiate: Certificate) => void
    testCertificate: (certificate: Certificate) => void
    showTestCertificateModal: boolean
    testCertificateResponse: string
    testCertificateName: string
    closeTestCertificateModal: () => void
    nameHandleChange: (event: any) => void
    protocolHandleChange: (event: any) => void
    deleteOnGetHandleChange: (event: any) => void
    isActiveHandleChange: (event: any) => void
    isANSIX12HandleChange: (event: any) => void
    usernameHandleChange: (event: any) => void
    passwordHandleChange: (event: any) => void
    senderIDHandleChange: (event: any) => void
    URLHandleChange: (event: any) => void
    portHandleChange: (event: any) => void
    receiveDirectoryHandleChange: (event: any) => void
    outboundDirectoryHandleChange: (event: any) => void
    sendDirectoryHandleChange: (event: any) => void
    signingGatewayNameHandleChange: (event: any) => void
    contactInfoHandleChange: (event: any) => void
}> = (props) => {
    // API for loading certificates
    const installCertificateURL = getAPIURL() + 'edi/InstallCertificate'

    // Certificate Modal states
    const [showAddCertificateModal, setShowAddCertificateModal] = React.useState(false)
    const [addCertificateError, setAddCertificateError] = useState(null)
    // const [loading, setLoading] = useState(true);

    // Certificate data initialization
    const [certificateData, setCertificateData] = React.useState<CertificateData>()
    const [expirationDate, setExpirationDate] = React.useState(getCurrentDate())
    const [certificateType, setCertificateType] = React.useState('PERSONAL')
    const [partnerOrSigning, setPartnerOrSigning] = React.useState('PARTNER')

    // Certificate sort - Descending by Expiration Date
    const initialSort: Array<SortDescriptor> = [{ field: 'ExpirationDate', dir: 'desc' }]
    const [sort, setSort] = React.useState(initialSort)

    // On File Drop handler
    const onDrop = async (e: React.DragEvent<HTMLDivElement>) => {
        e.stopPropagation()
        e.preventDefault()
        let reader = new FileReader()
        let droppedFile = e.dataTransfer.files[0]
        reader.addEventListener(
            'load',
            function () {
                let uri = reader.result
                setCertificateData({
                    FileData: (uri as string).split(',')[1],
                    CertificateFilename: droppedFile.name,
                    CertificateType: certificateType,
                })
            },
            false
        )
        reader.readAsDataURL(droppedFile)

        setShowAddCertificateModal(true)
    }

    // Cancel add certificate modal
    const cancelAddCertificateModal = () => {
        setShowAddCertificateModal(false)
        setAddCertificateError(null)
    }

    // Adding certificate locally once the user clicks "add certificate"
    function addCertificateLocally(
        fileData: string,
        certificateFilename: string,
        certificateType: string
    ) {
        props.gateway?.EDIGatewayCertificateView.push({
            GatewayName: props.gateway?.Name,
            ExpirationDate: formatDate(new Date(expirationDate), 'yyyy-MM-ddTHH:mm:ss'),
            PartnerCertificateName: partnerOrSigning === 'PARTNER' ? certificateFilename : '',
            SigningCertificateName: partnerOrSigning === 'SIGNING' ? certificateFilename : '',
        })

        setShowAddCertificateModal(false)

        setCertificateData({
            FileData: fileData,
            CertificateFilename: certificateFilename,
            CertificateType: certificateType,
        })
    }

    // Fetch to install certificate
    async function fetchInstallCertificate(
        fileData: string,
        certificateFilename: string,
        certificateType: string
    ) {
        await fetch(installCertificateURL, {
            method: 'POST',
            mode: 'cors',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                FileData: fileData,
                CertificateFilename: certificateFilename,
                CertificateType: certificateType,
            }),
        })
            .then((response) => {
                if (response.ok) {
                    setAddCertificateError(null)
                    addCertificateLocally(fileData, certificateFilename, certificateType)
                    setExpirationDate(getCurrentDate())
                    return response.json()
                }
                throw response
            })
            .catch((error) => {
                console.error('Error installing certificate: ', error)
                setAddCertificateError(error)
            })
    }

    // Handling when the user clicks the submit button
    const handleAddCertificateSubmit = (event: any) => {
        // Load certificate
        fetchInstallCertificate(
            event.values.FileData,
            event.values.CertificateFilename,
            certificateType
        )
    }

    const handleExpirationDateChange = React.useCallback(
        (e) => {
            const dateAsString = formatDate(new Date(e.value), 'yyyy-MM-ddTHH:mm:ss')
            setExpirationDate(dateAsString)
        },
        [setExpirationDate]
    )

    const handleCertificateTypeChange = React.useCallback(
        (e) => {
            setCertificateType(e.value)
        },
        [setCertificateType]
    )

    const handlePartnerOrSigningChange = React.useCallback(
        (e) => {
            setPartnerOrSigning(e.value)
        },
        [setPartnerOrSigning]
    )

    // Editing state
    const [showEditCertificateForm, setShowEditCertificateForm] = React.useState(false)
    const [editCertificateItem, setEditCertificateItem] = React.useState<Certificate>()

    // Get the row we want to edit
    const editCertificateRowClick = (event: any) => {
        setEditCertificateItem(event.dataItem)
        setExpirationDate(event.dataItem.ExpirationDate)
        enterEdit(event.dataItem)
    }

    // Edit form
    const enterEdit = (certificate: Certificate) => {
        setShowEditCertificateForm(true)
        setEditCertificateItem(certificate)
    }

    // Edit certificate cancel
    const editCertificateCancel = () => {
        setEditCertificateItem(undefined)
        setShowEditCertificateForm(false)
    }

    // Edit certificate submit
    const editCertificateSubmit = (event: any) => {
        props.setCertificate({
            GatewayName: event.values.GatewayName,
            ExpirationDate: expirationDate,
            PartnerCertificateName: event.values.PartnerCertificateName,
            SigningCertificateName: event.values.SigningCertificateName,
        })

        setShowEditCertificateForm(false)
    }
    // Test a specific certificate cell
    const TestCertificateCommandCell = (props: any) => (
        <TestCertificateCell {...props} testCertificate={testCertificate} />
    )

    // Test certificate on row we click on
    const testCertificate = (certificate: Certificate) => {
        props.testCertificate(certificate)
    }

    // Delete a specific certificate cell
    const DeleteCertificateCommandCell = (props: any) => (
        <DeleteCertificateCell {...props} deleteCertificate={deleteCertificate} />
    )

    // Delete certificate on row we click on
    const deleteCertificate = (certificate: Certificate) => {
        props.deleteCertificate(certificate)
    }

    return (
        <Dialog width='80%' height='90%'>
            <Form
                onSubmitClick={(event: any) => {
                    props.onSubmit(event)
                }}
                initialValues={props.gateway}
                render={(formRenderProps) => {
                    return (
                        <FormElement>
                            {/* <DialogActionsBar> */}
                            <div className={styles.wrapHeader}>
                                <div className={`k-form-buttons ${styles.buttonLeft}`}>
                                    <AWCButton
                                        type={BUTTON_TYPES.PRIMARY}
                                        onClick={formRenderProps.onSubmit}
                                        style={{
                                            width: 120,
                                        }}
                                    >
                                        Save Changes
                                    </AWCButton>
                                </div>

                                <div className={`k-form-buttons ${styles.buttonRight}`}>
                                    <AWCButton
                                        type={BUTTON_TYPES.SECONDARY}
                                        onClick={props.cancelDialog}
                                        style={{ width: 50 }}
                                    >
                                        <span className='k-icon k-i-close'></span>
                                    </AWCButton>
                                </div>
                            </div>
                            {/* </DialogActionsBar> */}

                            <div id='drop_zone' onDrop={onDrop}>
                                <div className={styles.wrap}>
                                    <FormInput
                                        length='short'
                                        label='Name'
                                        name='Name'
                                        required={true}
                                        maxChar={20}
                                        disabled={props.isNewGateway ? false : true}
                                        onChange={props.nameHandleChange}
                                    />
                                    <FormDropDownList
                                        label='Protocol'
                                        name='Protocol'
                                        onChange={props.protocolHandleChange}
                                    />
                                    <FormCheckbox
                                        label='Delete On Get'
                                        name='DeleteOnGetRequired'
                                        onChange={props.deleteOnGetHandleChange}
                                    />
                                    <FormCheckbox
                                        label='Is Active'
                                        name='isActive'
                                        onChange={() => {}}
                                    />
                                    <FormCheckbox
                                        label='Is ANSIX12'
                                        name='isANSIX12'
                                        onChange={() => {}}
                                    />
                                </div>

                                <div className={styles.wrap}>
                                    <FormInput
                                        label='Username'
                                        length='short'
                                        name='Username'
                                        maxChar={50}
                                        required={true}
                                        onChange={props.usernameHandleChange}
                                    />
                                    <FormInput
                                        label='Password'
                                        length='short'
                                        name='Password'
                                        maxChar={50}
                                        inputType='password'
                                        onChange={props.passwordHandleChange}
                                    />
                                    <FormInput
                                        length='short'
                                        label='Sender ID'
                                        maxChar={20}
                                        name='AS2SenderID'
                                        onChange={props.senderIDHandleChange}
                                    />
                                </div>

                                <div className={styles.wrap}>
                                    <FormInput
                                        label='URL'
                                        length='long'
                                        maxChar={200}
                                        name='URL'
                                        required={true}
                                        onChange={props.URLHandleChange}
                                    />
                                    <FormInput
                                        label='Port'
                                        length='shorter'
                                        maxChar={5}
                                        name='Port'
                                        onChange={props.portHandleChange}
                                    />
                                    <FormInput
                                        label='Receive Directory'
                                        length='short'
                                        maxChar={70}
                                        name='ProviderReceiveDirectory'
                                        onChange={props.receiveDirectoryHandleChange}
                                    />
                                </div>

                                <div className={styles.wrap}>
                                    <FormInput
                                        label='OutBound Directory'
                                        length='long'
                                        maxChar={70}
                                        name='OutBoundDirectory'
                                        onChange={props.outboundDirectoryHandleChange}
                                    />
                                    <FormInput
                                        label='Send Directory'
                                        length='short'
                                        maxChar={70}
                                        name='ProviderSendDirectory'
                                        onChange={props.sendDirectoryHandleChange}
                                    />
                                </div>

                                <div className={styles.block} />

                                <div className={styles.wrap}>
                                    <div>
                                        <div
                                            className={styles.bolded}
                                            style={{
                                                textAlign: 'left',
                                                paddingTop: 15,
                                                paddingBottom: 10,
                                            }}
                                        >
                                            Certificates
                                        </div>

                                        <Grid
                                            data={orderBy(
                                                props.gateway?.EDIGatewayCertificateView!,
                                                sort
                                            )}
                                            sortable={true}
                                            sort={sort}
                                            onSortChange={(e: GridSortChangeEvent) => {
                                                setSort(e.sort)
                                            }}
                                            onRowClick={editCertificateRowClick}
                                        >
                                            <GridColumn
                                                cell={TestCertificateCommandCell}
                                                title='Test'
                                                width='100px'
                                                sortable={false}
                                            />
                                            <GridColumn
                                                title='Expiration Date'
                                                field='ExpirationDate'
                                                cell={KendoGridDateCell}
                                                editable
                                            />
                                            <GridColumn
                                                title='Partner Cert Name'
                                                field='PartnerCertificateName'
                                                editable
                                            />
                                            <GridColumn
                                                title='Signing Cert Name'
                                                field='SigningCertificateName'
                                                editable
                                            />
                                            <GridColumn
                                                cell={DeleteCertificateCommandCell}
                                                title='Delete'
                                                width='100px'
                                                sortable={false}
                                            />
                                        </Grid>
                                    </div>

                                    <FormInput
                                        label='Signing Gateway Name'
                                        length='short'
                                        maxChar={20}
                                        name='SigningGatewayName'
                                        onChange={props.signingGatewayNameHandleChange}
                                    />
                                </div>

                                <div className={styles.block} />

                                <FormTextArea
                                    label='Notes'
                                    name='ContactInfo'
                                    maxChar={8000}
                                    onChange={props.contactInfoHandleChange}
                                />
                            </div>

                            <div className={styles.block} />
                        </FormElement>
                    )
                }}
            />
            {showAddCertificateModal && (
                <AddCertificateModal
                    certificateData={certificateData}
                    certificateType={certificateType}
                    expirationDate={expirationDate}
                    partnerOrSigning={partnerOrSigning}
                    addCertificateError={addCertificateError}
                    handleCertificateTypeChange={handleCertificateTypeChange}
                    handleExpirationDateChange={handleExpirationDateChange}
                    handlePartnerOrSigningChange={handlePartnerOrSigningChange}
                    cancelModal={cancelAddCertificateModal}
                    onSubmit={handleAddCertificateSubmit}
                />
            )}
            {showEditCertificateForm && (
                <EditCertificateModal
                    expirationDate={expirationDate}
                    certificate={editCertificateItem}
                    cancelEdit={editCertificateCancel}
                    handleExpirationDateChange={handleExpirationDateChange}
                    onSubmit={editCertificateSubmit}
                />
            )}
            {props.showTestCertificateModal && (
                <Dialog
                    title='AS2Test'
                    height='60%'
                    width='50%'
                    closeIcon
                    onClose={props.closeTestCertificateModal}
                >
                    <div
                        style={{
                            margin: '20px',
                            textAlign: 'center',
                        }}
                    >
                        <strong style={{ fontSize: 30 }}>{props.testCertificateName}</strong>
                        {props.testCertificateResponse === 'OK' ? (
                            <div style={{ textAlign: 'left', marginTop: 10 }}>Success</div>
                        ) : (
                            <div style={{ color: 'red', textAlign: 'left', marginTop: 10 }}>
                                Error: {props.testCertificateResponse}
                            </div>
                        )}
                    </div>
                </Dialog>
            )}
        </Dialog>
    )
}
